/**
 * Tutorial Service Singleton
 *
 * Manages tutorial registry, Driver.js instances, and progress tracking via API.
 * This is the infrastructure layer — tutorial definitions live in domains.
 */

import { reactive } from 'vue'
import { driver, type Driver } from 'driver.js'
import 'driver.js/dist/driver.css'
import { httpService } from './httpService'
import { permissionsService } from './permissionsService'
import { log } from '../utils/DebugLogger'

interface I18nInstance {
  global: { t: (key: string) => string }
}

let i18n: I18nInstance | null = null

const VALID_SLUGS = [
  'dashboard-overview',
  'tutorials-report-overview',
  'pdv-lista-overview',
  'pdv-checkout-overview',
  'fechamentos-overview',
  'plano-contas-overview',
] as const

export type TutorialSlug = typeof VALID_SLUGS[number]
export type TutorialAction = 'start' | 'complete' | 'skip' | 'step'
export type TutorialStatus = 'not_started' | 'started' | 'completed' | 'skipped'

export interface TutorialProgress {
  status: TutorialStatus
  step_reached: number
  replay_count: number
}

export interface TutorialStep {
  popover?: {
    title: string
    description: string
    [key: string]: unknown
  }
  [key: string]: unknown
}

export interface TutorialDefinition {
  slug: string
  title: string
  description: string
  nextBtnText: string
  prevBtnText: string
  doneBtnText: string
  steps: TutorialStep[]
  route?: string | RegExp
  allowedGroups?: string[]
}

export interface RouteTutorialInfo {
  slug: string
  title: string
  description: string
  status: TutorialStatus
  replay_count: number
}

interface TutorialState {
  initialized: boolean
  progress: Map<string, TutorialProgress>
  registry: Map<string, TutorialDefinition>
  activeTutorial: string | null
  loading: boolean
}

const tutorialState: TutorialState = reactive({
  initialized: false,
  progress: new Map<string, TutorialProgress>(),
  registry: new Map<string, TutorialDefinition>(),
  activeTutorial: null,
  loading: false,
})

let activeDriver: Driver | null = null

interface ProgressItem {
  tutorial_slug: string
  status: TutorialStatus
  step_reached: number | string
  replay_count: number | string
}

async function fetchProgress(): Promise<void> {
  try {
    tutorialState.loading = true
    const response = await httpService.get<{ success?: boolean; dados?: ProgressItem[] }>('/index.php/tutorial/progress')
    if (response?.success && response.dados) {
      tutorialState.progress.clear()
      for (const item of response.dados) {
        tutorialState.progress.set(item.tutorial_slug, {
          status: item.status,
          step_reached: parseInt(String(item.step_reached)) || 0,
          replay_count: parseInt(String(item.replay_count)) || 0,
        })
      }
      log('TutorialService', 'fetchProgress', 'Progress loaded', tutorialState.progress.size)
    }
  } catch (err) {
    log('TutorialService', 'fetchProgress', 'Failed to fetch progress', err)
  } finally {
    tutorialState.loading = false
  }
}

async function trackAction(slug: string, action: TutorialAction, step: number | null = null): Promise<void> {
  if (!(VALID_SLUGS as readonly string[]).includes(slug)) {
    log('TutorialService', 'trackAction', 'Invalid slug', slug)
    return
  }
  try {
    const payload: { slug: string; action: TutorialAction; step?: number } = { slug, action }
    if (step !== null) payload.step = step
    await httpService.post('/index.php/tutorial/track', payload)

    const current = tutorialState.progress.get(slug) || { status: 'not_started' as TutorialStatus, step_reached: 0, replay_count: 0 }
    const previousStatus = current.status
    if (action === 'start') {
      current.status = 'started'
      current.step_reached = 0
    } else if (action === 'complete') {
      current.status = 'completed'
      if (previousStatus === 'completed') {
        current.replay_count = (current.replay_count || 0) + 1
      }
    } else if (action === 'skip') {
      current.status = 'skipped'
    } else if (action === 'step' && step !== null) {
      current.step_reached = step
    }
    tutorialState.progress.set(slug, current)
  } catch (err) {
    log('TutorialService', 'trackAction', 'Failed to track', err)
  }
}

function registerTutorial(definition: TutorialDefinition): void {
  if (!(VALID_SLUGS as readonly string[]).includes(definition.slug)) {
    log('TutorialService', 'registerTutorial', 'Slug not in VALID_SLUGS, skipping', definition.slug)
    return
  }
  tutorialState.registry.set(definition.slug, definition)
  log('TutorialService', 'registerTutorial', 'Registered', definition.slug)
}

function translateDefinition(definition: TutorialDefinition): TutorialDefinition {
  if (!i18n) {
    log('TutorialService', 'translateDefinition', 'i18n not initialized, returning untranslated', definition.slug)
    return definition
  }

  const t = i18n.global.t

  return {
    ...definition,
    title: t(definition.title),
    description: t(definition.description),
    nextBtnText: t(definition.nextBtnText),
    prevBtnText: t(definition.prevBtnText),
    doneBtnText: t(definition.doneBtnText),
    steps: definition.steps.map(step => ({
      ...step,
      popover: step.popover ? {
        ...step.popover,
        title: t(step.popover.title),
        description: t(step.popover.description),
      } : undefined,
    })),
  }
}

function startTutorial(slug: string, forceReplay: boolean = false): void {
  const win = window as unknown as { __E2E_TEST_MODE?: boolean }
  if (win.__E2E_TEST_MODE || new URLSearchParams(window.location.search).has('e2e')) {
    log('TutorialService', 'startTutorial', 'Skipped — E2E test mode', slug)
    return
  }

  const definition = tutorialState.registry.get(slug)
  if (!definition) {
    log('TutorialService', 'startTutorial', 'No definition found for slug', slug)
    return
  }

  const userGroup = permissionsService.getUserGroup()
  if (definition.allowedGroups && !definition.allowedGroups.includes(userGroup)) {
    log('TutorialService', 'startTutorial', 'User group not allowed', { userGroup, allowed: definition.allowedGroups })
    return
  }

  if (!forceReplay) {
    const progress = tutorialState.progress.get(slug)
    if (progress && (progress.status === 'completed' || progress.status === 'skipped')) {
      log('TutorialService', 'startTutorial', 'Already completed/skipped', slug)
      return
    }
  }

  if (activeDriver) {
    activeDriver.destroy()
    activeDriver = null
  }

  tutorialState.activeTutorial = slug

  const translatedDef = translateDefinition(definition)

  activeDriver = driver({
    showProgress: true,
    animate: true,
    overlayColor: 'rgba(0, 0, 0, 0.6)',
    stagePadding: 8,
    stageRadius: 8,
    popoverClass: 'ff-tutorial-popover',
    nextBtnText: translatedDef.nextBtnText || 'Next',
    prevBtnText: translatedDef.prevBtnText || 'Previous',
    doneBtnText: translatedDef.doneBtnText || 'Done',
    onHighlightStarted: (_element, step) => {
      const stepIndex = translatedDef.steps.indexOf(step as TutorialStep)
      if (stepIndex >= 0) {
        trackAction(slug, 'step', stepIndex)
      }
    },
    onDestroyStarted: () => {
      if (activeDriver) {
        const currentStep = activeDriver.getActiveIndex() ?? 0
        const totalSteps = translatedDef.steps.length
        trackAction(slug, 'step', currentStep)
        if (currentStep >= totalSteps - 1) {
          trackAction(slug, 'complete')
        } else {
          trackAction(slug, 'skip')
        }
      }
      tutorialState.activeTutorial = null
      if (activeDriver) {
        activeDriver.destroy()
        activeDriver = null
      }
    },
    steps: translatedDef.steps as never,
  })

  trackAction(slug, 'start')
  activeDriver.drive()
}

function stopTutorial(): void {
  if (activeDriver) {
    activeDriver.destroy()
    activeDriver = null
    tutorialState.activeTutorial = null
  }
}

async function init(i18nInstance: I18nInstance): Promise<void> {
  if (tutorialState.initialized) return
  i18n = i18nInstance
  await fetchProgress()
  tutorialState.initialized = true
  log('TutorialService', 'init', 'Tutorial service initialized')
}

function getTutorialsForRoute(routePath: string): RouteTutorialInfo[] {
  const userGroup = permissionsService.getUserGroup()
  const tutorials: RouteTutorialInfo[] = []

  for (const [slug, def] of tutorialState.registry) {
    const routeMatches = typeof def.route === 'string'
      ? routePath === def.route || routePath.startsWith(def.route + '/')
      : def.route instanceof RegExp
        ? def.route.test(routePath)
        : false

    if (!routeMatches) continue

    if (def.allowedGroups && !def.allowedGroups.includes(userGroup)) continue

    const progress = tutorialState.progress.get(slug)
    tutorials.push({
      slug,
      title: def.title,
      description: def.description,
      status: progress?.status || 'not_started',
      replay_count: progress?.replay_count || 0,
    })
  }

  return tutorials
}

function isCompleted(slug: string): boolean {
  const progress = tutorialState.progress.get(slug)
  return progress?.status === 'completed'
}

export const tutorialService = {
  state: tutorialState,
  init,
  registerTutorial,
  startTutorial,
  stopTutorial,
  getTutorialsForRoute,
  isCompleted,
  fetchProgress,
  VALID_SLUGS,
}
